// frontend/src/pages/Candidats.js — Page admin gestion des candidatures
// Fix: téléchargement CV via fetch() avec JWT (pas window.location.href)

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../utils/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const STATUTS = [
  { val: 'nouveau',   label: 'Nouveau',   color: '#3b82f6', bg: '#eff6ff' },
  { val: 'en_cours',  label: 'En cours',  color: '#f59e0b', bg: '#fffbeb' },
  { val: 'entretien', label: 'Entretien', color: '#8b5cf6', bg: '#f5f3ff' },
  { val: 'retenu',    label: 'Retenu',    color: '#10b981', bg: '#ecfdf5' },
  { val: 'refuse',    label: 'Refusé',    color: '#ef4444', bg: '#fef2f2' },
];

const getStatut = (val) => STATUTS.find(s => s.val === val) || STATUTS[0];

const S = {
  page:    { padding: '32px', fontFamily: 'inherit' },
  header:  { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 32, flexWrap: 'wrap', gap: 16 },
  title:   { fontSize: 28, fontWeight: 900, color: '#0f172a', margin: 0 },
  card:    { background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', overflow: 'hidden' },
  badge:   (s) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color, border: `1px solid ${s.color}30` }),
  filters: { display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 20 },
  btn:     (active) => ({ padding: '6px 14px', borderRadius: 20, border: '1px solid', borderColor: active ? '#1e40af' : '#e2e8f0', background: active ? '#eff6ff' : 'white', color: active ? '#1e40af' : '#64748b', fontWeight: 700, fontSize: 12, cursor: 'pointer' }),
  table:   { width: '100%', borderCollapse: 'collapse' },
  th:      { padding: '12px 16px', textAlign: 'left', fontSize: 10, fontWeight: 700, color: '#94a3b8', background: '#f8fafc', textTransform: 'uppercase', letterSpacing: '0.1em', borderBottom: '1px solid #e2e8f0' },
  td:      { padding: '14px 16px', fontSize: 13, color: '#334155', borderBottom: '1px solid #f1f5f9', verticalAlign: 'middle' },
  action:  { padding: '6px 12px', borderRadius: 8, border: 'none', cursor: 'pointer', fontWeight: 700, fontSize: 11 },
  modal:   { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 },
  modalBox:{ background: 'white', borderRadius: 20, padding: 32, maxWidth: 640, width: '100%', maxHeight: '90vh', overflowY: 'auto' },
};

export default function Candidats() {
  const isMobile = useIsMobile();
  const { token } = useAuth();
  const config = { headers: { Authorization: `Bearer ${token}` } };

  const [candidats, setCandidats]     = useState([]);
  const [loading, setLoading]         = useState(true);
  const [filtreStatut, setFiltreStatut] = useState('');
  const [selected, setSelected]       = useState(null);
  const [notes, setNotes]             = useState('');
  const [saving, setSaving]           = useState(false);
  const [search, setSearch]           = useState('');
  const [downloading, setDownloading] = useState(null); // id_candidat en cours

  const fetchCandidats = useCallback(async () => {
    try {
      setLoading(true);
      const params = filtreStatut ? `?statut=${filtreStatut}` : '';
      const res = await axios.get(`${API_URL}/candidats${params}`, config);
      setCandidats(res.data.candidats || res.data || []);
    } catch (err) {
      console.error('Erreur chargement candidats:', err);
    } finally {
      setLoading(false);
    }
  }, [token, filtreStatut]);

  useEffect(() => { fetchCandidats(); }, [fetchCandidats]);

  const openModal = (c) => { setSelected(c); setNotes(c.notes_admin || ''); };

  const updateStatut = async (id, statut) => {
    try {
      await axios.patch(`${API_URL}/candidats/${id}`, { statut }, config);
      setCandidats(prev => prev.map(c => c.id_candidat === id ? { ...c, statut } : c));
      if (selected?.id_candidat === id) setSelected(prev => ({ ...prev, statut }));
    } catch (err) { console.error(err); }
  };

  const saveNotes = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      await axios.patch(`${API_URL}/candidats/${selected.id_candidat}`, { notes_admin: notes }, config);
      setCandidats(prev => prev.map(c => c.id_candidat === selected.id_candidat ? { ...c, notes_admin: notes } : c));
      setSelected(prev => ({ ...prev, notes_admin: notes }));
    } catch (err) { console.error(err); }
    setSaving(false);
  };

  const deleteCandidat = async (id) => {
    if (!window.confirm('⚠️ Supprimer définitivement ce candidat et son CV ?\nCette action est irréversible.')) return;
    try {
      await axios.delete(`${API_URL}/candidats/${id}`, config);
      setCandidats(prev => prev.filter(c => c.id_candidat !== id));
      if (selected?.id_candidat === id) setSelected(null);
    } catch (err) {
      alert('Erreur lors de la suppression : ' + (err.response?.data?.message || err.message));
    }
  };

  // ── Download CV via fetch + JWT (fix "Token manquant") ───────────────────
  const downloadCV = async (candidat) => {
    setDownloading(candidat.id_candidat);
    try {
      const res = await fetch(
        `${API_URL}/candidats/${candidat.id_candidat}/cv`,
        { headers: { Authorization: `Bearer ${token}` } }  // ← JWT dans le header
      );
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        alert(`Erreur téléchargement: ${err.message || res.status}`);
        return;
      }
      // Créer un lien de téléchargement depuis le Blob
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      a.href     = url;
      a.download = candidat.cv_original_name || `cv_${candidat.nom}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      alert('Erreur réseau lors du téléchargement');
      console.error(err);
    } finally {
      setDownloading(null);
    }
  };

  const filtered = candidats.filter(c => {
    if (!search) return true;
    const q = search.toLowerCase();
    return (c.nom?.toLowerCase().includes(q) || c.email?.toLowerCase().includes(q) || c.fonction?.toLowerCase().includes(q));
  });

  const counts = STATUTS.reduce((acc, s) => ({ ...acc, [s.val]: candidats.filter(c => c.statut === s.val).length }), {});

  return (
    <div style={{ ...S.page, padding: isMobile ? '14px 12px' : '32px' }}>
      <div style={{ ...S.header, flexDirection: isMobile ? 'column' : 'row', gap: isMobile ? 10 : undefined }}>
        <div>
          <h1 style={{ ...S.title, fontSize: isMobile ? 20 : 28 }}>👤 Candidatures</h1>
          <p style={{ fontSize: 13, color: '#64748b', marginTop: 6 }}>
            {candidats.length} candidature{candidats.length !== 1 ? 's' : ''} reçue{candidats.length !== 1 ? 's' : ''} — depuis mkw-solutions.be
          </p>
        </div>
        <input type="text" placeholder="🔍 Rechercher nom, email, fonction…"
          value={search} onChange={e => setSearch(e.target.value)}
          style={{ padding: '10px 16px', borderRadius: 12, border: '1px solid #e2e8f0', fontSize: 13, outline: 'none', minWidth: 260 }} />
      </div>

      {/* Filtres statut */}
      <div style={S.filters}>
        <button style={S.btn(!filtreStatut)} onClick={() => setFiltreStatut('')}>Tous ({candidats.length})</button>
        {STATUTS.map(s => (
          <button key={s.val} style={S.btn(filtreStatut === s.val)} onClick={() => setFiltreStatut(filtreStatut === s.val ? '' : s.val)}>
            {s.label} ({counts[s.val] || 0})
          </button>
        ))}
      </div>

      {/* Table */}
      <div style={S.card}>
        {loading ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>Chargement…</div>
        ) : filtered.length === 0 ? (
          <div style={{ padding: 48, textAlign: 'center', color: '#94a3b8' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>📭</div>
            <p style={{ fontWeight: 700 }}>Aucune candidature trouvée</p>
          </div>
        ) : isMobile ? (
          /* ── Mobile : cards ── */
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10, padding: '10px' }}>
            {filtered.map(c => {
              const st   = getStatut(c.statut);
              const date = new Date(c.date_candidature).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' });
              const isDown = downloading === c.id_candidat;
              return (
                <div key={c.id_candidat} style={{
                  background: 'white', borderRadius: 12, padding: '12px 14px',
                  border: '1px solid #e2e8f0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                  borderLeft: `3px solid ${st.color}` }}>
                  {/* Ligne 1 : Nom + Badge statut */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 5 }}>
                    <div>
                      <div style={{ fontWeight: 700, fontSize: 14, color: '#0f172a' }}>{c.nom}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{c.email}</div>
                      {c.telephone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.telephone}</div>}
                    </div>
                    <select value={c.statut} onChange={e => { e.stopPropagation(); updateStatut(c.id_candidat, e.target.value); }}
                      style={{ ...S.badge(st), border: 'none', cursor: 'pointer', appearance: 'none', outline: 'none',
                        fontSize: 10, flexShrink: 0, marginLeft: 8 }}>
                      {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                    </select>
                  </div>
                  {/* Ligne 2 : Fonction + Secteur + Date */}
                  <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11,
                    color: '#64748b', marginBottom: 8 }}>
                    <span style={{ fontWeight: 600, color: '#334155' }}>
                      {c.fonction || '—'}{c.secteur ? ` · ${c.secteur}` : ''}
                    </span>
                    <span style={{ color: '#94a3b8', flexShrink: 0, marginLeft: 8 }}>{date}</span>
                  </div>
                  {/* Ligne 3 : Boutons */}
                  <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                    {c.cv_filename && (
                      <button onClick={() => downloadCV(c)} disabled={isDown}
                        style={{ ...S.action, background: isDown ? '#e2e8f0' : '#1e40af',
                          color: isDown ? '#94a3b8' : 'white', fontSize: 11, padding: '5px 9px' }}>
                        {isDown ? '⏳ …' : '📄 CV'}
                      </button>
                    )}
                    <button onClick={() => openModal(c)}
                      style={{ ...S.action, background: '#f1f5f9', color: '#1e40af', fontSize: 11, padding: '5px 9px' }}>
                      👁 Fiche
                    </button>
                    <button onClick={e => { e.stopPropagation(); deleteCandidat(c.id_candidat); }}
                      style={{ ...S.action, background: '#fef2f2', color: '#dc2626', fontSize: 11, padding: '5px 9px' }}>
                      🗑️
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        ) : (
          /* ── Desktop : table originale ── */
          <table style={S.table}>
            <thead>
              <tr>
                <th style={S.th}>Date</th>
                <th style={S.th}>Candidat</th>
                <th style={S.th}>Fonction / Secteur</th>
                <th style={S.th}>Statut</th>
                <th style={S.th}>CV</th>
                <th style={S.th}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => {
                const st = getStatut(c.statut);
                const date = new Date(c.date_candidature).toLocaleDateString('fr-BE', { day: '2-digit', month: 'short', year: 'numeric' });
                const isDown = downloading === c.id_candidat;
                return (
                  <tr key={c.id_candidat}>
                    <td style={{ ...S.td, fontSize: 12, color: '#94a3b8', whiteSpace: 'nowrap' }}>{date}</td>
                    <td style={S.td}>
                      <div style={{ fontWeight: 700, color: '#0f172a' }}>{c.nom}</div>
                      <div style={{ fontSize: 11, color: '#64748b' }}>{c.email}</div>
                      {c.telephone && <div style={{ fontSize: 11, color: '#94a3b8' }}>{c.telephone}</div>}
                    </td>
                    <td style={{ ...S.td, fontSize: 12 }}>
                      <div style={{ fontWeight: 600 }}>{c.fonction || '—'}</div>
                      <div style={{ color: '#94a3b8', fontSize: 11 }}>{c.secteur || ''}</div>
                    </td>
                    <td style={S.td}>
                      <select value={c.statut} onChange={e => { e.stopPropagation(); updateStatut(c.id_candidat, e.target.value); }}
                        style={{ ...S.badge(st), border: 'none', cursor: 'pointer', appearance: 'none', outline: 'none' }}>
                        {STATUTS.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
                      </select>
                    </td>
                    <td style={S.td}>
                      {c.cv_filename ? (
                        <button onClick={() => downloadCV(c)} disabled={isDown}
                          style={{ ...S.action, background: isDown ? '#e2e8f0' : '#1e40af',
                            color: isDown ? '#94a3b8' : 'white' }}>
                          {isDown ? '⏳ …' : '📄 Télécharger'}
                        </button>
                      ) : <span style={{ fontSize: 11, color: '#94a3b8' }}>—</span>}
                    </td>
                    <td style={S.td}>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button onClick={() => openModal(c)} style={{ ...S.action, background: '#f1f5f9', color: '#1e40af' }}>
                          Voir fiche
                        </button>
                        <button onClick={e => { e.stopPropagation(); deleteCandidat(c.id_candidat); }}
                          style={{ ...S.action, background: '#fef2f2', color: '#dc2626', border: '1px solid #fecaca' }}
                          title="Supprimer définitivement">
                          🗑️
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Modal fiche candidat */}
      {selected && (
        <div style={S.modal} onClick={() => setSelected(null)}>
          <div style={S.modalBox} onClick={e => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
              <div>
                <h2 style={{ margin: 0, fontSize: 22, fontWeight: 900 }}>{selected.nom}</h2>
                <p style={{ margin: '4px 0 0', color: '#64748b', fontSize: 13 }}>{selected.email}</p>
              </div>
              <span style={S.badge(getStatut(selected.statut))}>{getStatut(selected.statut).label}</span>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 24 }}>
              {[['Téléphone', selected.telephone || '—'], ['Secteur', selected.secteur || '—'],
                ['Fonction', selected.fonction || '—'],
                ['Date', new Date(selected.date_candidature).toLocaleDateString('fr-BE', { dateStyle: 'long' })]
              ].map(([k, v]) => (
                <div key={k} style={{ background: '#f8fafc', borderRadius: 10, padding: '12px 16px', border: '1px solid #e2e8f0' }}>
                  <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.1em', marginBottom: 4 }}>{k}</div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: '#0f172a' }}>{v}</div>
                </div>
              ))}
            </div>

            {selected.message && (
              <div style={{ background: '#f8fafc', borderRadius: 10, padding: 16, border: '1px solid #e2e8f0', marginBottom: 24 }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', marginBottom: 8 }}>Message</div>
                <p style={{ margin: 0, fontSize: 13, color: '#334155', lineHeight: 1.6 }}>{selected.message}</p>
              </div>
            )}

            <div style={{ marginBottom: 20 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 8 }}>Changer le statut</label>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {STATUTS.map(st => (
                  <button key={st.val} onClick={() => updateStatut(selected.id_candidat, st.val)}
                    style={{ ...S.action, background: selected.statut === st.val ? st.color : '#f1f5f9', color: selected.statut === st.val ? 'white' : '#64748b', border: `1px solid ${selected.statut === st.val ? st.color : '#e2e8f0'}` }}>
                    {st.label}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#64748b', textTransform: 'uppercase', letterSpacing: '0.1em', display: 'block', marginBottom: 8 }}>Notes internes</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={4}
                placeholder="Impressions, questions, points à vérifier…"
                style={{ width: '100%', padding: 12, borderRadius: 10, border: '1px solid #e2e8f0', fontSize: 13, fontFamily: 'inherit', resize: 'vertical', outline: 'none', boxSizing: 'border-box' }} />
              <button onClick={saveNotes} disabled={saving}
                style={{ marginTop: 8, ...S.action, background: '#0f172a', color: 'white', padding: '8px 20px' }}>
                {saving ? 'Sauvegarde…' : '💾 Sauvegarder les notes'}
              </button>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'space-between', flexWrap: 'wrap' }}>
              {selected.cv_filename && (
                <button onClick={() => downloadCV(selected)} disabled={downloading === selected.id_candidat}
                  style={{ ...S.action, background: '#1e40af', color: 'white', padding: '10px 20px' }}>
                  {downloading === selected.id_candidat ? '⏳ Téléchargement…' : `📄 Télécharger le CV — ${selected.cv_original_name || 'cv.pdf'}`}
                </button>
              )}
              <button onClick={() => deleteCandidat(selected.id_candidat)}
                style={{ ...S.action, background: '#fef2f2', color: '#dc2626', padding: '10px 16px', border: '1px solid #fca5a5' }}>
                🗑️ Supprimer
              </button>
              <button onClick={() => setSelected(null)} style={{ ...S.action, background: '#f1f5f9', color: '#334155', padding: '10px 20px' }}>
                Fermer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
